import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api, { PLATFORM_NAME, errMsg, money } from "@/lib/api";
import { ridePath } from "@/lib/riderScope";
import { Button } from "@/components/ui/button";

export default function BookingPaid() {
  const { companyId } = useParams();
  const [params] = useSearchParams();
  const bookingId = params.get("booking_id");
  const [state, setState] = useState({ status: "polling", data: null });
  const [error, setError] = useState("");

  useEffect(() => {
    if (!bookingId) return;
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/public/bookings/${bookingId}/deposit-status`);
        if (data.payment_status === "paid") { setState({ status: "paid", data }); return; }
      } catch (e) { setError(errMsg(e)); return; }
      if (attempts < 10) setTimeout(tick, 2000);
      else setState({ status: "pending", data: null });
    };
    tick();
  }, [bookingId]);

  return (
    <div data-testid="booking-paid-page" className="flex min-h-screen items-center justify-center bg-ink-900 px-4 text-slate-200">
      <div className="w-full max-w-md rounded-xl border border-ink-600 bg-ink-800 p-8 text-center">
        {state.status === "polling" && (
          <>
            <Loader2 className="mx-auto h-6 w-6 animate-spin text-gold" />
            <p className="mt-5 text-sm text-slate-400">Confirming your deposit…</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-400" />
            <h1 className="mt-5 text-2xl font-bold text-slate-50">Deposit received</h1>
            <p className="mt-2 text-sm text-slate-400">
              {money(state.data.amount_paid)} paid. Any remaining balance is charged after your trip
              is completed.
            </p>
          </>
        )}
        {state.status === "pending" && (
          <>
            <XCircle className="mx-auto h-8 w-8 text-amber-400" />
            <h1 className="mt-5 text-2xl font-bold text-slate-50">Payment still processing</h1>
            <p className="mt-2 text-sm text-slate-400">Your reservation request is saved. The operator will confirm by email.</p>
          </>
        )}
        {error && <p className="mt-4 text-sm text-red-400">{error}</p>}
        <Button asChild className="mt-7 w-full bg-gold text-ink-900 hover:bg-gold-light">
          <Link to={companyId ? ridePath(companyId) : "/"} data-testid="booking-paid-home">
            {companyId ? "Back to your trips" : `Back to ${PLATFORM_NAME}`}
          </Link>
        </Button>
      </div>
    </div>
  );
}
